import React from "react";
import PropTypes from "prop-types";
import TextInput from "./TextInput";
import { Alert } from "reactstrap";
import { Formik, Form, Field } from "formik";
import { faPlus } from "@fortawesome/fontawesome-free-solid";
import FontAwesomeIcon from "@fortawesome/react-fontawesome";
import Button from "./CustomButton";
import SelectInput from "./SelectInput";
import Checkbox from "./CustomCheckbox";
import { onChangeCheckbox } from "../helpers/index";
import { invalidNumber } from "../helpers/index";
import { toNumber } from "lodash";
import { compose, setDisplayName } from "recompose";
import NextForm from "../containers/NextForm";

const expressRateTypes = [
  {
    label: "Flat Fee",
    value: "flat"
  },
  {
    label: "Percentage",
    value: "percent"
  }
];

export const DealerForm = props => {
  const {
    onSubmit,
    submittingDealer,
    failSubmitDealer,
    initialValues,
    successSubmitDealer,
    inputs,
    focusNextField
  } = props;
  return (
    <Formik
      onSubmit={onSubmit}
      validate={validate}
      initialValues={initialValues}
      render={({ values, setValues }) => (
        <Form>
          <Field
            innerRef={input => (inputs.name = input)}
            onKeyPress={focusNextField("email")}
            name="name"
            type="text"
            component={TextInput}
            placeholder="Dealer's Name"
            label="Name"
          />
          <Field
            innerRef={input => (inputs.email = input)}
            onKeyPress={focusNextField("phone_number")}
            name="email"
            type="email"
            component={TextInput}
            placeholder="Dealer's Email"
            label="Email"
          />
          <Field
            innerRef={input => (inputs.phone_number = input)}
            onKeyPress={focusNextField("express_rate")}
            name="phone_number"
            type="text"
            component={TextInput}
            placeholder="Dealer's Phone Number"
            label="Phone Number"
          />
          <Field
            innerRef={input => (inputs.express_rate = input)}
            onKeyPress={focusNextField("express_rate_type")}
            name="express_rate"
            type="text"
            component={TextInput}
            placeholder="Dealer's Express Rate"
            label="Express Rate"
          />
          <Field
            innerRef={input => (inputs.express_rate_type = input)}
            onKeyPress={focusNextField("express_quantity")}
            name="express_rate_type"
            component={SelectInput}
            placeholder="Dealer's Express Rate Type"
            label="Express Rate Type"
            source={expressRateTypes}
          />
          <Field
            innerRef={input => (inputs.express_quantity = input)}
            onKeyPress={focusNextField("logo_url")}
            name="express_quantity"
            type="text"
            component={TextInput}
            placeholder="Dealer's Maximum Express Quantity"
            label="Express Quantity"
          />
          <Field
            innerRef={input => (inputs.logo_url = input)}
            onKeyPress={focusNextField("website_url")}
            name="logo_url"
            type="text"
            component={TextInput}
            placeholder="Dealer's Logo URL"
            label="Logo URL"
          />
          <Field
            innerRef={input => (inputs.website_url = input)}
            name="website_url"
            type="text"
            component={TextInput}
            placeholder="Dealer's Website URl"
            label="Website URl"
          />
          <Field
            innerRef={input => (inputs.tax_rate = input)}
            onKeyPress={focusNextField("online_pay")}
            name="tax_rate"
            type="text"
            component={TextInput}
            placeholder="7.00"
            label="Tax Rate"
          />
          <Checkbox
            number="online_pay"
            name="online_pay"
            label="Online Payment Method"
            checked={values.online_pay}
            onChange={onChangeCheckbox({
              name: "online_pay",
              setValues,
              values
            })}
          />
          <Checkbox
            number="notify_by_sms"
            name="notify_by_sms"
            label="Notify by SMS"
            checked={values.notify_by_sms}
            onChange={onChangeCheckbox({
              name: "notify_by_sms",
              setValues,
              values
            })}
          />

          {failSubmitDealer && <Alert color="danger">{failSubmitDealer}</Alert>}
          {successSubmitDealer && (
            <Alert color="success">
              Successful Update!{" "}
              <span role="img" aria-label="grin">
                &#128513;
              </span>
            </Alert>
          )}
          <Button
            fill
            type="submit"
            disabled={submittingDealer}
            color="primary"
          >
            Submit <FontAwesomeIcon icon={faPlus} />
          </Button>
        </Form>
      )}
    />
  );
};

DealerForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  submittingDealer: PropTypes.bool.isRequired,
  failSubmitDealer: PropTypes.string,
  initialValues: PropTypes.object.isRequired
};

DealerForm.defaultProps = {
  initialValues: {
    name: "",
    phone_number: "",
    email: "",
    express_rate: "",
    express_rate_type: "",
    express_quantity: "",
    logo_url: "",
    website_url: "",
    notify_by_sms: false,
    online_pay: false
  }
};

export const validate = (values = {}) => {
  const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
  const urlRegex = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/;
  const imageURLRegex = /(https?:\/\/.*\.(?:png|jpg))/i;
  const errors = {};
  if (!values.name) {
    errors.name = "Required";
  }
  if (!values.phone_number) {
    errors.phone_number = "Required";
  }
  if (!values.email) {
    errors.email = "Required";
  } else if (!emailRegex.test(values.email)) {
    errors.email = "Invalid email";
  }
  if (values.logo_url && !imageURLRegex.test(values.logo_url)) {
    errors.logo_url = "Invalid URL";
  }
  if (values.website_url && !urlRegex.test(values.website_url)) {
    errors.website_url = "Invalid URL";
  }
  if (!values.express_rate_type) {
    errors.express_rate_type = "Required";
  } else if (!values.express_rate) {
    errors.express_rate = "Required";
  } else if (
    invalidNumber(values.express_rate) ||
    (values.express_rate_type === "percent" &&
      toNumber(values.express_rate) >= 100)
  ) {
    errors.express_rate = "Invalid express rate";
  }
  if (!values.express_quantity) {
    errors.express_quantity = "Required";
  } else if (invalidNumber(values.express_quantity)) {
    errors.express_quantity = "Invalid express quantity";
  }
  return errors;
};

export default compose(
  setDisplayName("DealerForm"),
  NextForm
)(DealerForm);
